import {createContext, ReactNode, useCallback, useContext, useState} from 'react'

// Groups the viewer has folded away, remembered in this browser only.
const COLLAPSED_KEY = 'hrm.sidebar.collapsed'

const readCollapsed = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]') as string[])
  } catch {
    return new Set()
  }
}

const writeCollapsed = (collapsed: Set<string>) => {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(collapsed)))
  } catch {
    // Storage unavailable: the fold still works, it just isn't remembered.
  }
}

type SidebarGroupsContextType = {
  isOpen: (key: string) => boolean
  toggle: (key: string) => void
  open: (key: string) => void
}

const SidebarGroupsContext = createContext<SidebarGroupsContextType>({
  isOpen: () => true,
  toggle: () => {},
  open: () => {},
})

export const useSidebarGroups = () => useContext(SidebarGroupsContext)

/**
 * Open/closed state of the sidebar groups. Every group starts OPEN; clicking its heading folds it.
 * Kept in React rather than left to Metronic's accordion script, which toggles a `show` class that
 * React's next render of the item's className would silently put back.
 */
export const SidebarGroupsProvider = ({children}: {children: ReactNode}) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed)

  const update = useCallback((fn: (next: Set<string>) => void) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      fn(next)
      writeCollapsed(next)
      return next
    })
  }, [])

  const isOpen = useCallback((key: string) => !collapsed.has(key), [collapsed])
  const toggle = useCallback(
    (key: string) => update((next) => (next.has(key) ? next.delete(key) : next.add(key))),
    [update]
  )
  const open = useCallback(
    (key: string) => {
      if (collapsed.has(key)) update((next) => next.delete(key))
    },
    [collapsed, update]
  )

  return (
    <SidebarGroupsContext.Provider value={{isOpen, toggle, open}}>
      {children}
    </SidebarGroupsContext.Provider>
  )
}
